import { Card, Checkbox, Datepicker, Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow, WeekStart } from 'flowbite-react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { QueryResult } from '../../components/Alerts.tsx';
import ProtectedRoute from '../../components/ProtectedRoute.tsx';
import { useAppDispatch, useAppSelector } from '../../lib/hooks.ts';
import { selectDate } from '../../lib/model/date/dateSlice.ts';
import { useMonthlyReportGroupedByWeekQuery } from '../../lib/model/kantoordagApi.ts';


export default function MonthReportGroupedByWeek() {
    const user = useAppSelector(state => state.user.user)
    const dateStr = useAppSelector(state => state.date.date)
    const date = DateTime.fromISO(dateStr)
    const dispatch = useAppDispatch()
    const { t, i18n } = useTranslation("month-report");

    const monthReportGroupedByWeekQuery = useMonthlyReportGroupedByWeekQuery({
        datum: dateStr,
        userId: user?.email ?? ""
    })

    function dateChanged(date: Date | null) {
        if (date) {
            dispatch(selectDate(DateTime.fromJSDate(date)))
        }
    }

    return (
        <ProtectedRoute authenticated={true}>
            <h1>{t('month_report')}</h1>
            <Datepicker onChange={dateChanged}
                        language={i18n.language}
                        className={"w-full"}
                        weekStart={WeekStart.Monday}
                        value={date.toJSDate()}/>
            <Card>
                <QueryResult query={monthReportGroupedByWeekQuery} message={t("loading_failed")}>
                    {
                        monthReportGroupedByWeekQuery.data != null ?
                            <div className="overflow-x-auto max-h-96 relative">
                                <Table className="min-w-full">
                                    <TableHead className="sticky top-0 bg-white dark:bg-gray-800 z-10">
                                        <TableHeadCell>{t("week")}</TableHeadCell>
                                        <TableHeadCell>{t("day")}</TableHeadCell>
                                        <TableHeadCell>{t("in_office")}</TableHeadCell>
                                        <TableHeadCell>{t("at_home")}</TableHeadCell>
                                    </TableHead>
                                    <TableBody>
                                        {monthReportGroupedByWeekQuery.data.weeks.map(week => (
                                            <>
                                                {week.days.map((day, dayIndex) => (
                                                    <TableRow key={`${week.weekNumber}-${day.date}`}
                                                              className="bg-white dark:bg-gray-800 border-0">
                                                        {dayIndex === 0 && (
                                                            <TableCell
                                                                rowSpan={week.days.length + 1}
                                                                className="whitespace-nowrap font-medium text-gray-900 dark:text-white"
                                                                style={{ borderBottom: 'none' }}>
                                                                {t("week")} {week.weekNumber}
                                                            </TableCell>
                                                        )}
                                                        <TableCell style={{ borderBottom: 'none' }}>
                                                            {DateTime.fromISO(day.date).toFormat('EEE dd')}
                                                        </TableCell>
                                                        <TableCell style={{ borderBottom: 'none' }}>
                                                            <Checkbox checked={day.inOffice} disabled/>
                                                        </TableCell>
                                                        <TableCell style={{ borderBottom: 'none' }}>
                                                            <Checkbox checked={!day.inOffice} disabled/>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {/* Weekly totals row */}
                                                <TableRow className="bg-white dark:bg-gray-800 font-bold">
                                                    <TableCell style={{
                                                        borderTop: '2px solid #E5E7EB',
                                                        borderBottom: '2px solid #E5E7EB'
                                                    }}>{t("weekly_totals")}</TableCell>
                                                    <TableCell style={{
                                                        borderTop: '2px solid #E5E7EB',
                                                        borderBottom: '2px solid #E5E7EB'
                                                    }}>{week.daysInOffice}</TableCell>
                                                    <TableCell style={{
                                                        borderTop: '2px solid #E5E7EB',
                                                        borderBottom: '2px solid #E5E7EB'
                                                    }}>{week.daysAtHome}</TableCell>
                                                </TableRow>
                                            </>
                                        ))}
                                        {/* Monthly totals row */}
                                        <TableRow className="bg-white dark:bg-gray-800 font-bold text-lg">
                                            <TableCell colSpan={2} style={{
                                                borderTop: '2px solid #E5E7EB',
                                                borderBottom: '2px solid #E5E7EB'
                                            }}>{t("monthly_totals")}</TableCell>
                                            <TableCell style={{
                                                borderTop: '2px solid #E5E7EB',
                                                borderBottom: '2px solid #E5E7EB'
                                            }}>{monthReportGroupedByWeekQuery.data.totalDaysInOffice}</TableCell>
                                            <TableCell style={{
                                                borderTop: '2px solid #E5E7EB',
                                                borderBottom: '2px solid #E5E7EB'
                                            }}>{monthReportGroupedByWeekQuery.data.totalDaysAtHome}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </div>
                            : null
                    }
                </QueryResult>
            </Card>
        </ProtectedRoute>
    )
}